// Bestellung als PDF im Layout des Kalkulation-Angebots (gleiche CI: Logo, blauer
// Tabellenkopf, Zebra-Zeilen, Fußzeile mit Bank-/Gerichtsangaben).
// Vektor-PDF über jsPDF (lazy geladen) → klein, scharf, als Mail-Anhang geeignet.
// Bewusst OHNE interne Bezüge (BANF-Nr., interne Referenzen) — nur, was der Lieferant braucht.

import type { MailAnhang } from '../components/MailEntwurf';
import { LOGO_PNG_B64 } from '../components/logoSickinger';

export type BestellPdfPos = {
  nr: number | string;
  artikelnr?: string | null;      // Art.-Nr. des Lieferanten
  bezeichnung: string;
  menge: string;
  einheit?: string | null;
  bemerkung?: string | null;
  liefertermin?: string | null;
};

export type BestellPdfDaten = {
  lieferant: string;
  ansprechpartner?: string | null;
  bestellnr: string;
  datum?: string;                  // ISO, Standard: heute
  liefertermin?: string | null;    // Gesamt-Liefertermin (optional)
  positionen: BestellPdfPos[];
  materialzeugnis?: boolean;       // Blech/Material: „Materialzeugnis beilegen“
  zusatzHinweise?: string[];       // weitere Hinweise (z. B. Schweißen: „Zeugnisse immer erforderlich“)
  bearbeiter?: { name: string; tel: string; email: string };
};

// CI-Farben wie in der Kalkulation (offer.tsx)
const CI: [number, number, number] = [27, 94, 158];        // #1B5E9E
const CI_HELL: [number, number, number] = [238, 244, 251]; // #EEF4FB
const GRAU: [number, number, number] = [100, 116, 139];
const DUNKEL: [number, number, number] = [30, 41, 59];
const LINIE: [number, number, number] = [226, 232, 240];

const FIRMA = {
  name: 'Manfred Sickinger GmbH & Co.KG',
  strasse: 'Mönchswiesen 12',
  ort: '71735 Eberdingen',
  tel: '07042 - 7098',
  fax: '07042 - 7033',
  email: 'info@apparatebau-sickinger.de',
  web: 'www.Apparatebau-Sickinger.de',
  gericht: 'Erfüllungsort u. Gerichtsstand für beide Teile: Stuttgart',
  ust: 'USt-Id.Nr. DE145002701',
  bank: 'KSK Ludwigsburg',
  iban: 'IBAN DE71 6045 0050 0009 8049 84',
  bic: 'BIC SOLADES1LBG',
  gf: 'Geschäftsführer: Uwe Sickinger · Ralph Sickinger',
};

export const BESTELLER_STANDARD = {
  name: 'Jasmin Sickinger',
  tel: '+49 7042/96820122',
  email: 'j.sickinger@apparatebau-sickinger.de',
};

export function datumDE(iso?: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : (iso || '');
}

// Seitenverhältnis des Logos aus dem PNG-Header (IHDR: Breite/Höhe ab Byte 16)
function logoVerhaeltnis(b64: string): number {
  try {
    const b = atob(b64.slice(0, 48));
    const u32 = (o: number) => ((b.charCodeAt(o) << 24) | (b.charCodeAt(o + 1) << 16) | (b.charCodeAt(o + 2) << 8) | b.charCodeAt(o + 3)) >>> 0;
    const w = u32(16), h = u32(20);
    return w > 0 && h > 0 ? w / h : 3.4;
  } catch { return 3.4; }
}

// jsPDF nachladen; nach einem Deploy kann ein offener Tab veraltete Dateien haben → klare Meldung
async function jsPdfLaden() {
  const modul = await import('jspdf');
  if (!modul?.jsPDF) {
    throw new Error('Die Seite wurde inzwischen aktualisiert. Bitte mit Strg+F5 neu laden und den PDF-Knopf erneut drücken — die Bestellung ist gespeichert.');
  }
  return modul.jsPDF;
}
export function bestellPdfVorladen() { jsPdfLaden().catch(() => { /* beim Erzeugen erneut versucht */ }); }

export async function bestellPdf(d: BestellPdfDaten) {
  const jsPDF = await jsPdfLaden();
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const L = 14, R = 196, W = R - L;
  const SEITE_H = 297;
  const FUSS_Y = 272;               // Oberkante der Fußzeile
  const Y_MAX = FUSS_Y - 6;         // Inhalt endet hier
  const bearbeiter = d.bearbeiter ?? BESTELLER_STANDARD;
  const datum = datumDE(d.datum ?? new Date().toISOString().slice(0, 10));

  type Opt = { size?: number; bold?: boolean; align?: 'left' | 'right' | 'center'; farbe?: [number, number, number] };
  const text = (t: string, x: number, y: number, o: Opt = {}) => {
    pdf.setFont('helvetica', o.bold ? 'bold' : 'normal');
    pdf.setFontSize(o.size ?? 10);
    pdf.setTextColor(...(o.farbe ?? DUNKEL));
    pdf.text(t, x, y, { align: o.align ?? 'left' });
  };
  const umbruch = (t: string, breite: number, size: number, bold = false): string[] => {
    pdf.setFont('helvetica', bold ? 'bold' : 'normal'); pdf.setFontSize(size);
    return (t || '').split('\n').flatMap(z => (z.trim() === '' ? [''] : (pdf.splitTextToSize(z, breite) as string[])));
  };

  // ── Briefkopf (auf jeder Seite): Logo links, Firmenblock rechts ──
  const kopf = () => {
    const logoH = 17, logoW = logoH * logoVerhaeltnis(LOGO_PNG_B64);
    pdf.addImage('data:image/png;base64,' + LOGO_PNG_B64, 'PNG', L, 11, logoW, logoH);
    text(FIRMA.name, R, 14, { size: 8, bold: true, align: 'right' });
    text(`${FIRMA.strasse} · ${FIRMA.ort}`, R, 18, { size: 7.5, farbe: GRAU, align: 'right' });
    text(`Tel.: ${FIRMA.tel} · Fax: ${FIRMA.fax}`, R, 21.8, { size: 7.5, farbe: GRAU, align: 'right' });
    text(FIRMA.email, R, 25.6, { size: 7.5, farbe: GRAU, align: 'right' });
    text(FIRMA.web, R, 29.4, { size: 7.5, farbe: GRAU, align: 'right' });
  };

  // ── Fußzeile (auf jeder Seite): gesetzliche Angaben ──
  const fuss = () => {
    pdf.setDrawColor(...CI); pdf.setLineWidth(0.5);
    pdf.line(L, FUSS_Y, R, FUSS_Y);
    const sp = W / 3;
    const spalte = (x: number, zeilen: { t: string; bold?: boolean }[]) =>
      zeilen.forEach((z, i) => text(z.t, x, FUSS_Y + 4.5 + i * 3.4, { size: 7, bold: z.bold, farbe: z.bold ? DUNKEL : GRAU }));
    spalte(L, [{ t: FIRMA.name, bold: true }, { t: FIRMA.strasse }, { t: FIRMA.ort }, { t: FIRMA.gericht }]);
    spalte(L + sp + 2, [{ t: `Tel.: ${FIRMA.tel}` }, { t: `Fax: ${FIRMA.fax}` }, { t: FIRMA.email }, { t: FIRMA.web }, { t: FIRMA.ust }]);
    spalte(L + 2 * sp + 4, [{ t: FIRMA.bank, bold: true }, { t: FIRMA.iban }, { t: FIRMA.bic }, { t: FIRMA.gf }]);
  };

  kopf();

  // ── Empfänger links, Beleg-Box rechts ──
  let y = 44;
  text(`${FIRMA.name} · ${FIRMA.strasse} · ${FIRMA.ort}`, L, y, { size: 6.5, farbe: GRAU });
  pdf.setDrawColor(203, 213, 225); pdf.setLineWidth(0.2);
  pdf.line(L, y + 1.2, L + 92, y + 1.2);
  text(d.lieferant || '—', L, y + 7, { size: 11, bold: true });
  if (d.ansprechpartner?.trim()) text(`z. Hd. ${d.ansprechpartner.trim()}`, L, y + 12, { size: 9, farbe: GRAU });

  const boxZeilen: [string, string][] = [['Bestell-Nr.', d.bestellnr || '—'], ['Datum', datum]];
  if (d.liefertermin?.trim()) boxZeilen.push(['Liefertermin', d.liefertermin.trim()]);
  const bx = 134, bw = R - bx, bh = 6.2;
  boxZeilen.forEach(([l, w], i) => {
    const by = y - 2 + i * bh;
    pdf.setFillColor(248, 250, 252); pdf.rect(bx, by, 26, bh, 'F');
    pdf.setDrawColor(203, 213, 225); pdf.setLineWidth(0.2); pdf.rect(bx, by, bw, bh);
    text(l, bx + 1.8, by + 4.2, { size: 8, farbe: GRAU });
    text(w, R - 1.8, by + 4.2, { size: 8.5, bold: i === 0, align: 'right' });
  });

  // ── Titel + Einleitung ──
  y = 78;
  text(`Bestellung Nr. ${d.bestellnr}`, L, y, { size: 16, bold: true, farbe: CI });
  y += 8;
  text('Sehr geehrte Damen und Herren,', L, y, { size: 10 });
  y += 5.5;
  text('hiermit bestellen wir bei Ihnen die folgenden Positionen:', L, y, { size: 10 });
  y += 5;

  // ── Bearbeiter-Zeile ──
  pdf.setDrawColor(...LINIE); pdf.setLineWidth(0.25);
  pdf.line(L, y, R, y); pdf.line(L, y + 6.5, R, y + 6.5);
  let bxp = L;
  const feld = (label: string, wert: string) => {
    text(label, bxp, y + 4.4, { size: 8, farbe: GRAU });
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
    bxp += pdf.getTextWidth(label + ' ') + 0.6;
    text(wert, bxp, y + 4.4, { size: 8, bold: true });
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8);
    bxp += pdf.getTextWidth(wert) + 8;
  };
  feld('Bearbeiter:', bearbeiter.name);
  feld('Telefon:', bearbeiter.tel);
  feld('E-Mail:', bearbeiter.email);
  y += 11;

  // ── Positionstabelle ──
  const mitTermin = d.positionen.some(p => (p.liefertermin ?? '').trim());
  const wPos = 12, wArt = 32, wMenge = 18, wME = 12, wTermin = mitTermin ? 26 : 0;
  const xPos = L, xArt = xPos + wPos, xBez = xArt + wArt;
  const wBez = W - wPos - wArt - wMenge - wME - wTermin;
  const xMenge = xBez + wBez, xME = xMenge + wMenge, xTermin = xME + wME;

  const tabellenKopf = () => {
    pdf.setFillColor(...CI); pdf.rect(L, y, W, 7, 'F');
    const t = (s: string, x: number, o: Opt = {}) => text(s, x, y + 4.8, { size: 8.5, bold: true, farbe: [255, 255, 255], ...o });
    t('Pos.', xPos + 2); t('Ihre Art.-Nr.', xArt + 2); t('Bezeichnung', xBez + 2);
    t('Menge', xMenge + wMenge - 2, { align: 'right' }); t('ME', xME + 2);
    if (mitTermin) t('Liefertermin', xTermin + 2);
    y += 7;
  };
  tabellenKopf();

  d.positionen.forEach((p, i) => {
    const bez = umbruch(p.bezeichnung || '—', wBez - 4, 9, true);
    const bem = (p.bemerkung ?? '').trim() ? umbruch(p.bemerkung!.trim(), wBez - 4, 8) : [];
    const art = umbruch(p.artikelnr ?? '', wArt - 4, 8.5);
    const zeilen = Math.max(bez.length + bem.length, art.length, 1);
    const h = Math.max(7, zeilen * 4 + 3);
    if (y + h > Y_MAX) {
      pdf.addPage(); kopf(); y = 40; tabellenKopf();
    }
    if (i % 2 === 1) { pdf.setFillColor(...CI_HELL); pdf.rect(L, y, W, h, 'F'); }
    pdf.setDrawColor(...LINIE); pdf.setLineWidth(0.2); pdf.line(L, y + h, R, y + h);
    text(String(p.nr), xPos + 2, y + 4.7, { size: 8.5, farbe: GRAU });
    art.forEach((z, k) => text(z, xArt + 2, y + 4.7 + k * 4, { size: 8.5 }));
    bez.forEach((z, k) => text(z, xBez + 2, y + 4.7 + k * 4, { size: 9, bold: true }));
    bem.forEach((z, k) => text(z, xBez + 2, y + 4.7 + (bez.length + k) * 4, { size: 8, farbe: GRAU }));
    text(p.menge, xMenge + wMenge - 2, y + 4.7, { size: 9, bold: true, align: 'right' });
    text(p.einheit ?? '', xME + 2, y + 4.7, { size: 8.5 });
    if (mitTermin) text(p.liefertermin ?? '', xTermin + 2, y + 4.7, { size: 8.5 });
    y += h;
  });
  y += 7;

  // ── Hinweise + Schluss (zusammenhängend; bei Platzmangel neue Seite) ──
  const hinweise: string[] = [
    'Bitte senden Sie uns eine Auftragsbestätigung mit verbindlichem Liefertermin.',
  ];
  if (d.materialzeugnis) hinweise.push('Bitte legen Sie der Lieferung das Materialzeugnis bei.');
  for (const z of d.zusatzHinweise ?? []) if (z.trim()) hinweise.push(z.trim());
  hinweise.push(`Lieferanschrift: ${FIRMA.name}, ${FIRMA.strasse}, ${FIRMA.ort}`);
  const blockH = hinweise.length * 4.8 + 30;
  if (y + blockH > Y_MAX) { pdf.addPage(); kopf(); y = 42; }
  text('Hinweise: ', L, y, { size: 9, bold: true, farbe: DUNKEL });
  hinweise.forEach((z, i) => text(z, L + 18, y + i * 4.8, { size: 9, farbe: GRAU }));
  y += hinweise.length * 4.8 + 5;
  text('Vielen Dank für Ihre Bemühungen. Für Rückfragen stehen wir Ihnen jederzeit gerne zur Verfügung.', L, y, { size: 10 });
  y += 8;
  text('Mit freundlichen Grüßen aus Eberdingen', L, y, { size: 10 });
  y += 6;
  text(bearbeiter.name, L, y, { size: 10 });
  y += 5;
  text(FIRMA.name, L, y, { size: 10, bold: true });

  // ── Fußzeile + Seitenzahl auf allen Seiten ──
  const n = pdf.getNumberOfPages();
  for (let s = 1; s <= n; s++) {
    pdf.setPage(s);
    fuss();
    text(`Seite ${s} von ${n}`, R, SEITE_H - 5, { size: 7, farbe: GRAU, align: 'right' });
  }
  return pdf;
}

// Dateiname strikt ASCII (Outlook/Chromium), z. B. „Bestellung_DV-4711_Wuerth.pdf“
export function bestellPdfName(bestellnr: string, lieferant: string): string {
  const ascii = (s: string) => s
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
    .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue').replace(/ß/g, 'ss')
    .replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  return `Bestellung_${ascii(bestellnr) || 'neu'}_${ascii(lieferant) || 'Lieferant'}.pdf`;
}

// PDF erzeugen und als Mail-Anhang (Base64) + Speicher-Funktion zurückgeben
export async function bestellPdfErzeugen(d: BestellPdfDaten): Promise<{ anhang: MailAnhang; speichern: () => void }> {
  const pdf = await bestellPdf(d);
  const name = bestellPdfName(d.bestellnr, d.lieferant);
  const b64 = (pdf.output('datauristring') as string).split('base64,')[1] ?? '';
  return { anhang: { name, mime: 'application/pdf', b64 }, speichern: () => { pdf.save(name); } };
}
